import React, { useEffect, useState, useRef } from "react";
import "./DataAPIOperatorAgentInstructions.css";

const DataAPIOperatorAgentInstructions = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Ensures it triggers only once
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`api-integration-container ${isVisible ? "visible" : ""}`}
      ref={containerRef}
    >
      <h1 className="api-integration-title">Implementation Details</h1>

      {/* Step 1 */}
      <div className="api-integration-row">
        <div className="api-integration-step">
          <h3 className="step-title">Step 1</h3>
          <p className="step-description">Choose Your API</p>
        </div>
        <div className="api-integration-details">
          <p>Determine the <strong>Data API</strong> that you want to connect to your custom AI Agents; enabling <strong>retrieval-augmented generation (RAG)</strong> for intelligent decision-making.</p>
          <p>This demo showcases the <strong>Wikipedia API</strong>, but our system works with any API.</p>
        </div>
      </div>

      {/* Step 2 */}
      <div className="api-integration-row">
        <div className="api-integration-step">
          <h3 className="step-title">Step 2</h3>
          <p className="step-description">Define Your API Schema</p>
        </div>
        <div className="api-integration-details">
          <p>Prepare an API schema aligned with our Data API Operator Agent’s requirements.</p>
          <p>Our agent utilizes elements like:</p>
          <ul className="api-integration-list">
            <li><strong>Template URLs</strong> for structured API calls</li>
            <li><strong>Endpoint Descriptions</strong> for retrieval logic</li>
            <li><strong>Filter Types</strong> for precise data extraction</li>
            <li><strong>Error Handling & Rate Limits</strong> to manage responses</li>
          </ul>
          <div className="api-code-box">
            <code>
{`{
  "base_url": "https://en.wikipedia.org/{endpoint}",
  "endpoints": {
    "search": {
      "description": "Search for Wikipedia pages related to a query and return a list of possible page titles.",
      "url_format": "/w/api.php?action=query&list=search&srsearch={query}&format=json",
      "filters": {
        "query": {
          "type": "string",
          "description": "Search term used to find Wikipedia pages.",
          "valid_format": "Full sentence, phrase, or keywords.",
          "validation": "free_text",
          "api_specific_format": "Spaces must be replaced with %20."
        }
      },
      "example_request": "https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=history%20of%20artificial%20intelligence&format=json"
    },
    "summary": ...,
    "page_content": ...
  },
  "pagination": ...,
  "authentication": ...,
  "error_handling": ...
}
`}
            </code>
          </div>
        </div>
      </div>

      {/* Step 3 */}
      <div className="api-integration-row">
        <div className="api-integration-step">
          <h3 className="step-title">Step 3</h3>
          <p className="step-description">Integrate into AI Agents</p>
        </div>
        <div className="api-integration-details">
          <p>Our agent <strong>analyzes prompts</strong> and your <strong>API schema</strong> to identify the data your RAG system needs without needing to analyze your raw data itself.</p>
          <p>This process automatically generates API request URLs for efficient retrieval, privately within your own computing environment.</p>
          <p>Example returned API URL for the Wikipedia API:</p>
          <div className="api-code-box">
            <code>
{`https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=artificial%20intelligence&format=json`}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataAPIOperatorAgentInstructions;
